import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, MoreHorizontal, ExternalLink, Copy, Archive, Clock, TrendingDown, Sparkles } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { useIsMobile } from '@/hooks/useMediaQuery'
import { PriorityStatusBadge } from './PriorityStatusBadge'
import { useArchivePriority, useDuplicatePriority } from '@/hooks/usePriorities'
import { getBucketIcon } from '@/lib/bucketIcons'
import { todayISO } from '@/lib/dates'
import { isReviewOverdue as reviewOverdue } from '@/lib/priorities'
import { useNotesByPriority } from '@/hooks/useNotes'
import { useTagsForPriority } from '@/hooks/useValueTags'
import { cn } from '@/lib/utils'
import type { Priority } from '@/types'

// ─── Labels ───────────────────────────────────────────────────────────────────

const ORIGIN_LABEL: Record<string, string> = {
  chosen: '★',
  obligation: '⟳',
}
const ENERGY_LABEL: Record<string, string> = {
  gives: '↑',
  drains: '↓',
}

interface PriorityTileProps {
  priority: Priority
  bucketColor: string
  bucketIcon?: string
  onOpenDetail: () => void
  draggingDisabled?: boolean
  coachingTeaser?: string
  bucketMisaligned?: boolean
}

/**
 * A priority in its area's list: a read-only summary. Tapping the title opens the edit view,
 * and the ⋯ menu is the only other control. Status, cadence, notes and the title are all
 * changed in the edit view. The whole left edge is the drag handle.
 */
export function PriorityTile({ priority, bucketColor, bucketIcon, onOpenDetail, draggingDisabled, coachingTeaser, bucketMisaligned }: PriorityTileProps) {
  const isDormant = priority.status === 'dormant'
  const archivePriority = useArchivePriority()
  const duplicatePriority = useDuplicatePriority()
  const isMobile = useIsMobile()

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: priority.id,
    disabled: draggingDisabled,
  })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  }

  const effectiveColor = priority.color ?? bucketColor
  const BucketIconEl = getBucketIcon(bucketIcon)
  const tags  = useTagsForPriority(priority.id)
  const notes = useNotesByPriority(priority.id)

  const latestNoteSnippet = (() => {
    const body = notes?.[0]?.body
    if (!body) return null
    const line = body.split('\n')[0].trim()
    return line.length > 72 ? line.slice(0, 70) + '…' : line
  })()

  const isReviewOverdue = reviewOverdue(priority, todayISO())

  const actionsMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-10 w-10 md:h-7 md:w-7 max-md:[&_svg]:size-6 text-muted-foreground hover:text-foreground"
          aria-label={`Actions for ${priority.title}`}
        >
          <MoreHorizontal size={13} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-40">
        <DropdownMenuItem onClick={onOpenDetail}>
          <ExternalLink size={13} className="mr-2" />
          Edit details
        </DropdownMenuItem>
        {/* Phones: just Edit and Archive */}
        <DropdownMenuItem className="hidden md:flex" onClick={() => duplicatePriority.mutate(priority.id)}>
          <Copy size={13} className="mr-2" />
          Duplicate
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => archivePriority.mutate(priority.id)}
          className="text-destructive focus:text-destructive"
        >
          <Archive size={13} className="mr-2" />
          Archive
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )

  // Phones show the status under the title (with the menu), so the title gets the full width.
  const showMetaRow = isMobile || priority.origin || priority.energy === 'gives' || priority.energy === 'drains'
    || priority.loadWeight || isReviewOverdue || (bucketMisaligned && !isDormant)

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'group flex items-center gap-2 p-3 rounded-lg border border-border bg-card shadow-sm mb-4 transition-all duration-150 hover:shadow-md hover:-translate-y-[1px] cursor-default',
        isDormant && 'opacity-55',
      )}
    >
      {/* Drag handle: the tile's whole left edge, top to bottom, with the grip centred */}
      {!draggingDisabled ? (
        <button
          {...attributes}
          {...listeners}
          className="group/handle self-stretch -my-3 -ml-3 pl-3 pr-1.5 max-md:pr-2 flex items-center rounded-l-lg text-muted-foreground cursor-grab active:cursor-grabbing touch-none flex-shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={`Drag to reorder ${priority.title}`}
          tabIndex={0}
        >
          <GripVertical size={14} className="max-md:opacity-40 opacity-0 group-hover:opacity-40 group-hover/handle:!opacity-100 transition-opacity" />
        </button>
      ) : (
        <span className="w-[14px] flex-shrink-0" aria-hidden />
      )}

      {/* Bucket icon cue */}
      {BucketIconEl && (
        <BucketIconEl size={12} style={{ color: effectiveColor }} className="flex-shrink-0 opacity-60" aria-hidden />
      )}

      {/* Main content */}
      <div className="flex-1 min-w-0">
        {/* Row 1: title (opens the edit view) */}
        <button
          type="button"
          onClick={onOpenDetail}
          className="text-sm font-medium text-left block w-full break-words md:truncate focus-visible:outline-none hover:underline underline-offset-2"
          aria-label={`Open detail: ${priority.title}`}
        >
          {priority.title}
        </button>

        {/* Row 2: metadata chips (phones: status first, and the menu at the end) */}
        {showMetaRow && (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-0.5 max-md:mt-1.5">
            {isMobile && <PriorityStatusBadge status={priority.status} />}
            {priority.origin && (
              <span
                className="text-xs text-muted-foreground"
                title={priority.origin === 'chosen' ? 'Chosen' : 'Obligation'}
                aria-label={priority.origin === 'chosen' ? 'Chosen' : 'Obligation'}
              >
                {ORIGIN_LABEL[priority.origin]}
              </span>
            )}
            {(priority.energy === 'gives' || priority.energy === 'drains') && (
              <span
                className={cn('text-xs', priority.energy === 'gives' ? 'text-emerald-600' : 'text-amber-600')}
                title={priority.energy === 'gives' ? 'Gives energy' : 'Drains energy'}
                aria-label={priority.energy === 'gives' ? 'Gives energy' : 'Drains energy'}
              >
                {ENERGY_LABEL[priority.energy]}
              </span>
            )}
            {priority.loadWeight && (
              <span className="flex gap-0.5" aria-label={`Load weight ${priority.loadWeight} of 5`}>
                {Array.from({ length: 5 }).map((_, i) => (
                  <span
                    key={i}
                    className={cn(
                      'inline-block w-1.5 h-1.5 rounded-full',
                      i < priority.loadWeight! ? 'bg-foreground/40' : 'bg-foreground/10',
                    )}
                  />
                ))}
              </span>
            )}
            {isReviewOverdue && (
              <span
                className="flex items-center gap-0.5 text-[10px] text-amber-600 dark:text-amber-400"
                title={`Review was due ${priority.nextReviewDate}`}
                aria-label={`Review overdue since ${priority.nextReviewDate}`}
              >
                <Clock size={9} />
                Review due
              </span>
            )}
            {/* FR-D2 — misalignment indicator */}
            {bucketMisaligned && !isDormant && (
              <span
                className="flex items-center gap-0.5 text-[10px] text-amber-600 dark:text-amber-400"
                title="Life area is under-allocated vs. its target"
                aria-label="Area under-allocated"
              >
                <TrendingDown size={9} />
                Under-target
              </span>
            )}
            {isMobile && <div className="ml-auto -my-2 -mr-1.5">{actionsMenu}</div>}
          </div>
        )}

        {/* Row 3: value tags */}
        {tags && tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {tags.map((tag) => (
              <span
                key={tag.id}
                className="inline-block text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground border border-border/60"
              >
                {tag.label}
              </span>
            ))}
          </div>
        )}

        {/* Row 4: Latest note glance */}
        {latestNoteSnippet && !coachingTeaser && (
          <p className="text-[11px] text-muted-foreground/55 mt-0.5 truncate" aria-label={`Latest note: ${latestNoteSnippet}`}>
            {latestNoteSnippet}
          </p>
        )}

        {/* Row 5: the coach's note on this priority, from the latest letter */}
        {coachingTeaser && (
          <p className="flex gap-1.5 mt-1 text-[11px] text-foreground italic leading-relaxed" aria-label={`Coach's note: ${coachingTeaser}`}>
            <Sparkles size={11} className="flex-shrink-0 mt-[3px] text-muted-foreground" aria-hidden />
            <span className="line-clamp-2">{coachingTeaser}</span>
          </p>
        )}
      </div>

      {/* Larger screens: the status (a label, not a control) and the menu, pinned right */}
      {!isMobile && (
        <div className="flex items-center gap-1.5 flex-shrink-0 self-center">
          <PriorityStatusBadge status={priority.status} />
          {actionsMenu}
        </div>
      )}
    </div>
  )
}
